import { DiceError, DiceExpression, type RollResult } from '../dice/dice.ts';
import type { Random } from '../dice/random.ts';
import { EntryEditError } from '../edits.ts';
import { javaStrip } from '../javaText.ts';
import { int, scalar, text, transaction, type SqlConnection, type SqlRow } from '../sql.ts';
import { findBuiltIn } from '../sheets/builtInTemplates.ts';
import { SheetEvaluator } from '../sheets/sheetEvaluator.ts';
import { CONDITIONS_KEY, allFields, condition, decodeConditions, encodeConditions, fieldFor, type CharacterSheet, type Condition, type SheetTemplate } from '../sheets/sheetModel.ts';
import { getSheet, getTemplate, listTemplates, setSheetValues, sheetForEntry } from '../sheets/sheets.ts';
import { ENCOUNTER_DIFFICULTIES, getCampaign, type EncounterDifficulty } from './campaigns.ts';

/**
 * The Game Master Toolkit's Run screen (docs/PARITY-STEP-5-PLAN.md section 8), as the desktop's
 * `campaign.TurnOrder`, `campaign.Combat` and `ui.SheetBridge`:
 * - turns in initiative order, highest first, the roster position breaking ties; combatants
 *   who are down are skipped, though player characters at 0 still get their turn; a timed
 *   condition counts down at the end of its combatant's turn;
 * - damage never below 0 (unless no maximum is set), healing never above the maximum;
 * - a combatant who shares their character sheet (a player character, or the only combatant
 *   linked to their entry) keeps hit points and conditions in step with it, both ways;
 * - everything that happens goes into the current session's play log, "[20:41] ...".
 *
 * Rolls use the `Random` passed in, and are handed back so the page can add them to the Dice
 * Roller's history.
 */

export interface Combatant {
  readonly id: string;
  readonly name: string;
  readonly linkedEntryId: string | null;
  readonly initiative: number;
  readonly maxHp: number;
  readonly currentHp: number;
  readonly playerCharacter: boolean;
  readonly notes: string;
  readonly position: number;
  readonly conditions: readonly Condition[];
}

export interface Encounter {
  readonly id: string;
  readonly campaignId: string;
  readonly sessionId: string | null;
  readonly name: string;
  readonly description: string;
  readonly difficulty: EncounterDifficulty;
  /** 0 until it's started. */
  readonly round: number;
  readonly currentCombatantId: string | null;
  /** By roster position. */
  readonly combatants: readonly Combatant[];
  readonly created: string;
  readonly modified: string;
}

/** What happened, in words, and the rolls made for it. */
export interface RunResult {
  readonly events: readonly string[];
  readonly rolls: readonly { readonly label: string; readonly result: RollResult }[];
}

export interface CombatantDetails {
  readonly name: string;
  readonly linkedEntryId: string | null;
  readonly initiative: number;
  readonly maxHp: number;
  readonly currentHp: number;
  readonly playerCharacter: boolean;
  readonly notes: string;
}

export type RunCommand =
  | { readonly kind: 'start' | 'next' | 'previous' | 'end' | 'rollInitiative' | 'quickAdd' }
  | { readonly kind: 'damage' | 'heal'; readonly combatantId: string; readonly amount: string }
  | { readonly kind: 'addCondition'; readonly combatantId: string; readonly name: string; readonly rounds: number }
  | { readonly kind: 'removeCondition'; readonly combatantId: string; readonly name: string }
  | { readonly kind: 'setInitiative'; readonly combatantId: string; readonly initiative: number }
  | { readonly kind: 'addCombatant'; readonly details: CombatantDetails }
  | { readonly kind: 'updateCombatant'; readonly combatantId: string; readonly details: CombatantDetails }
  | { readonly kind: 'removeCombatant'; readonly combatantId: string };

/** Offered when a combatant has no sheet and no template in the realm lists any conditions. */
export const GENERIC_CONDITIONS = ['Blinded', 'Frightened', 'Poisoned', 'Prone', 'Stunned', 'Unconscious'] as const;
/** How long a condition can be set to last: until removed, or a number of rounds. */
export const CONDITION_DURATIONS = [0, 1, 2, 3, 10] as const;
const NAME_MAX = 200;
const NOTES_MAX = 4000;
const HP_LIMIT = 9999;

// ---- reading and saving ----

export function getEncounter(conn: SqlConnection, id: string): Encounter | null {
  const row = conn.all('SELECT id, campaign_id, session_id, name, description, difficulty, round, current_combatant_id, created, modified FROM campaign_encounters WHERE id = ?', [id])[0];
  if (!row) return null;
  const difficulty = text(row['difficulty']);
  return {
    id,
    campaignId: text(row['campaign_id']),
    sessionId: optional(row['session_id']),
    name: text(row['name']),
    description: text(row['description']),
    difficulty: (ENCOUNTER_DIFFICULTIES as readonly string[]).includes(difficulty) ? (difficulty as EncounterDifficulty) : 'MEDIUM',
    round: int(row['round']),
    currentCombatantId: optional(row['current_combatant_id']),
    combatants: conn
      .all('SELECT id, name, linked_entry_id, initiative, max_hp, current_hp, is_player_character, notes, position, conditions FROM encounter_combatants WHERE encounter_id = ? ORDER BY position', [id])
      .map(combatantOf),
    created: text(row['created']),
    modified: text(row['modified']),
  };
}

/** Saves the encounter's turn and its combatants, as `CampaignRepository.saveEncounter` does. */
function saveEncounter(conn: SqlConnection, encounter: Encounter, now: string): void {
  conn.run('UPDATE campaign_encounters SET round = ?, current_combatant_id = ?, modified = ? WHERE id = ?', [encounter.round, encounter.currentCombatantId, now, encounter.id]);
  conn.run('DELETE FROM encounter_combatants WHERE encounter_id = ?', [encounter.id]);
  for (const c of encounter.combatants) {
    conn.run(
      `INSERT INTO encounter_combatants (id, encounter_id, position, name, linked_entry_id, initiative, max_hp, current_hp, is_player_character, notes, conditions)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [c.id, encounter.id, c.position, c.name, c.linkedEntryId, c.initiative, c.maxHp, c.currentHp, c.playerCharacter ? 1 : 0, c.notes, encodeConditions(c.conditions)],
    );
  }
}

/** The encounter the Run screen runs (`setTable`), keeping the current session. Null for none. */
export function setActiveEncounter(conn: SqlConnection, campaignId: string, encounterId: string | null, now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    if (!getCampaign(conn, campaignId)) throw new EntryEditError('not_found', 'That campaign no longer exists.');
    if (encounterId !== null && int(scalar(conn, 'SELECT COUNT(*) FROM campaign_encounters WHERE id = ? AND campaign_id = ?', [encounterId, campaignId])) === 0) {
      throw new EntryEditError('not_found', 'That encounter no longer exists.');
    }
    conn.run('UPDATE campaigns SET active_encounter_id = ?, modified = ? WHERE id = ?', [encounterId, now, campaignId]);
  });
}

/**
 * Adds a line to the campaign's current session's play log, "[20:41] Warlord Kessian takes 7
 * (58/65)." (`appendToPlayLog`); nothing when no session is current. `stamp` is the time here.
 */
export function appendToPlayLog(conn: SqlConnection, campaignId: string, line: string, stamp: string, now: string = new Date().toISOString()): void {
  const sessionId = getCampaign(conn, campaignId)?.currentSessionId ?? null;
  if (sessionId === null) return;
  const existing = scalar(conn, 'SELECT play_log FROM campaign_sessions WHERE id = ?', [sessionId]);
  if (existing === null) return;
  const before = text(existing).replace(/[\s\u0000- ]+$/u, '');
  conn.run('UPDATE campaign_sessions SET play_log = ?, modified = ? WHERE id = ?', [`${before === '' ? '' : `${before}\n`}[${stamp}] ${javaStrip(line)}`, now, sessionId]);
}

// ---- turns (TurnOrder) ----

/** Everyone in turn order: initiative, highest first, then roster position. */
export function turnOrder(combatants: readonly Combatant[]): Combatant[] {
  return [...combatants].sort((a, b) => b.initiative - a.initiative || a.position - b.position);
}

/** Out of the fight: hit points to lose, and none left. */
export const isDown = (c: Combatant) => c.maxHp > 0 && c.currentHp <= 0;
/** Takes turns: anyone not down, and player characters even when they are. */
export const acts = (c: Combatant) => !isDown(c) || c.playerCharacter;

interface Step {
  readonly encounter: Encounter;
  readonly events: string[];
}

export function startTurns(encounter: Encounter): Step {
  const first = turnOrder(encounter.combatants).find(acts);
  const events = [`${encounter.name} begins - round 1.`];
  if (first) events.push(`${first.name}'s turn.`);
  return { encounter: { ...encounter, round: 1, currentCombatantId: first?.id ?? null }, events };
}

/** Ends the current turn (its timed conditions count down) and moves to the next who acts, a new round after the last. */
export function nextTurn(encounter: Encounter): Step {
  if (encounter.round <= 0) return startTurns(encounter);
  const events: string[] = [];
  let working = encounter;
  const currentId = encounter.currentCombatantId;
  if (currentId !== null) working = tickConditions(working, currentId, events);
  const ordered = turnOrder(working.combatants);
  const index = currentId === null ? -1 : ordered.findIndex((c) => c.id === currentId);
  let round = working.round;
  for (let step = 1; step <= ordered.length; step++) {
    const candidate = index + step;
    const next = ordered[candidate % ordered.length]!;
    if (acts(next)) {
      if (candidate >= ordered.length) {
        round++;
        events.push(`Round ${round}.`);
      }
      events.push(`${next.name}'s turn.`);
      return { encounter: { ...working, round, currentCombatantId: next.id }, events };
    }
  }
  events.push('No one is left standing to take a turn.');
  return { encounter: { ...working, round, currentCombatantId: null }, events };
}

/** Back to the previous combatant who acts (a turn taken by mistake); conditions aren't un-ticked. */
export function previousTurn(encounter: Encounter): Step {
  if (encounter.round <= 0) return { encounter, events: [] };
  const ordered = turnOrder(encounter.combatants);
  const found = encounter.currentCombatantId === null ? -1 : ordered.findIndex((c) => c.id === encounter.currentCombatantId);
  const index = found < 0 ? 0 : found;
  let round = encounter.round;
  for (let step = 1; step <= ordered.length; step++) {
    const candidate = index - step;
    const previous = ordered[((candidate % ordered.length) + ordered.length) % ordered.length]!;
    if (acts(previous)) {
      if (candidate < 0) {
        if (round <= 1) return { encounter, events: [] };
        round--;
      }
      return { encounter: { ...encounter, round, currentCombatantId: previous.id }, events: [`Back to ${previous.name}'s turn (round ${round}).`] };
    }
  }
  return { encounter, events: [] };
}

export function endTurns(encounter: Encounter): Step {
  const rounds = encounter.round === 1 ? '1 round' : `${encounter.round} rounds`;
  return { encounter: { ...encounter, round: 0, currentCombatantId: null }, events: [`${encounter.name} ends after ${rounds}.`] };
}

function tickConditions(encounter: Encounter, combatantId: string, events: string[]): Encounter {
  return replaceAll(
    encounter,
    encounter.combatants.map((c) => {
      if (c.id !== combatantId) return c;
      const remaining: Condition[] = [];
      for (const k of c.conditions) {
        if (k.roundsLeft <= 0) remaining.push(k);
        else if (k.roundsLeft > 1) remaining.push(condition(k.name, k.roundsLeft - 1));
        else events.push(`${c.name} is no longer ${k.name}.`);
      }
      return { ...c, conditions: remaining };
    }),
  );
}

// ---- damage, healing and conditions (Combat) ----

const hpSuffix = (c: Combatant) => (c.maxHp > 0 ? ` (${c.currentHp}/${c.maxHp})` : '');

export function damage(c: Combatant, amount: number): { combatant: Combatant; event: string } {
  const before = c.currentHp;
  const after = c.maxHp > 0 ? Math.max(0, before - amount) : before - amount;
  const changed = { ...c, currentHp: after };
  const downed = before > 0 && after <= 0 && c.maxHp > 0;
  return { combatant: changed, event: `${c.name} takes ${amount}${hpSuffix(changed)}${downed ? (c.playerCharacter ? ' and drops to 0 hit points!' : ' and is down.') : '.'}` };
}

export function heal(c: Combatant, amount: number): { combatant: Combatant; event: string } {
  const after = c.maxHp > 0 ? Math.min(c.maxHp, Math.max(0, c.currentHp) + amount) : c.currentHp + amount;
  const changed = { ...c, currentHp: after };
  return { combatant: changed, event: `${c.name} heals ${amount}${hpSuffix(changed)}.` };
}

/** Adds a condition, replacing one of the same name (so re-applying Frightened resets its rounds). */
export function addCondition(c: Combatant, added: Condition): { combatant: Combatant; event: string } {
  const conditions = [...c.conditions.filter((k) => k.name.toLowerCase() !== added.name.toLowerCase()), added];
  const label = added.roundsLeft === 0 ? added.name : `${added.name} (${added.roundsLeft}${added.roundsLeft === 1 ? ' round)' : ' rounds)'}`;
  return { combatant: { ...c, conditions }, event: `${c.name} is ${label}.` };
}

export function removeCondition(c: Combatant, name: string): { combatant: Combatant; event: string } {
  return { combatant: { ...c, conditions: c.conditions.filter((k) => k.name.toLowerCase() !== name.toLowerCase()) }, event: `${c.name} is no longer ${name}.` };
}

/** Whether a combatant's hit points and conditions are the sheet's own: a player character, or the only one linked to the entry. */
export function sharesSheet(encounter: Encounter, c: Combatant): boolean {
  if (c.linkedEntryId === null) return false;
  if (c.playerCharacter) return true;
  return encounter.combatants.filter((x) => x.linkedEntryId === c.linkedEntryId).length === 1;
}

// ---- the sheet link (SheetBridge) ----

interface SheetAndTemplate {
  readonly sheet: CharacterSheet;
  readonly template: SheetTemplate;
}

function sheetOf(conn: SqlConnection, entryId: string | null): SheetAndTemplate | null {
  if (entryId === null) return null;
  const sheet = sheetForEntry(conn, entryId);
  const template = sheet ? getTemplate(conn, sheet.templateId) : null;
  return sheet && template ? { sheet, template } : null;
}

const evaluator = (s: SheetAndTemplate) => new SheetEvaluator(s.template, s.sheet.values);

/** `conditionsFor`: the template's own, or its built-in's when a built-in copy lists none. */
export function templateConditions(template: SheetTemplate): readonly string[] {
  if (template.conditions.length > 0 || template.builtinKey === '') return template.conditions;
  return findBuiltIn(template.builtinKey)?.conditions ?? [];
}

/** The conditions to offer a combatant: their sheet's template's, else any the realm's templates list, else the usual few. */
export function conditionPresets(conn: SqlConnection, entryId: string | null): readonly string[] {
  const own = sheetOf(conn, entryId);
  if (own) {
    const listed = templateConditions(own.template);
    if (listed.length > 0) return listed;
  }
  const all = [...new Set(listTemplates(conn).flatMap((t) => templateConditions(t)))];
  return all.length > 0 ? all : GENERIC_CONDITIONS;
}

/** Initiative rolled from the entry's sheet, if it has one that says how. */
function sheetInitiative(conn: SqlConnection, entryId: string | null, random: Random): RollResult | null {
  const s = sheetOf(conn, entryId);
  if (!s) return null;
  try {
    return evaluator(s).rollInitiative(random);
  } catch (error) {
    if (error instanceof DiceError) return null;
    throw error;
  }
}

function sheetHitPoints(conn: SqlConnection, entryId: string | null): [number, number] | null {
  const s = sheetOf(conn, entryId);
  const hp = s ? evaluator(s).hitPoints() : null;
  return hp ? [Number(hp[0]), Number(hp[1])] : null;
}

/** Writes a combatant's hit points and conditions to their sheet, when they share it. */
function pushToSheet(conn: SqlConnection, encounter: Encounter, c: Combatant, now: string): void {
  if (!sharesSheet(encounter, c)) return;
  const s = sheetOf(conn, c.linkedEntryId);
  if (!s) return;
  const values = new Map(s.sheet.values);
  const hpField = fieldFor(s.template, 'HIT_POINTS');
  if (c.maxHp > 0 && hpField && evaluator(s).hitPoints()) values.set(hpField.key, String(c.currentHp));
  const encoded = encodeConditions(c.conditions);
  if (encoded === '') values.delete(CONDITIONS_KEY);
  else values.set(CONDITIONS_KEY, encoded);
  setSheetValues(conn, s.sheet.id, values, now);
}

/** A combatant as their sheet says they are now, when they share it. */
function pullFromSheet(conn: SqlConnection, encounter: Encounter, c: Combatant): Combatant {
  if (!sharesSheet(encounter, c)) return c;
  const s = sheetOf(conn, c.linkedEntryId);
  if (!s) return c;
  const hp = evaluator(s).hitPoints();
  const pulled = hp ? { ...c, currentHp: Number(hp[0]), maxHp: Math.max(0, Number(hp[1])) } : c;
  return { ...pulled, conditions: decodeConditions(s.sheet.values.get(CONDITIONS_KEY)) };
}

// ---- running an encounter ----

/**
 * One thing done at the table: a turn, a hit, a condition, a combatant added or changed.
 * It's saved at once, the sheet link kept, and what happened written to the play log.
 */
export function runEncounter(
  conn: SqlConnection,
  campaignId: string,
  encounterId: string,
  command: RunCommand,
  /** `newId` names added combatants (fixed only in tests). */
  options: { readonly random: Random; readonly stamp: string; readonly now?: string; readonly newId?: () => string },
): RunResult {
  const now = options.now ?? new Date().toISOString();
  const newId = options.newId ?? (() => crypto.randomUUID());
  const rolls: { label: string; result: RollResult }[] = [];
  const events: string[] = [];
  transaction(conn, () => {
    const found = getEncounter(conn, encounterId);
    if (!found || found.campaignId !== campaignId) throw new EntryEditError('not_found', 'That encounter no longer exists.');
    let encounter: Encounter = found;
    const combatant = (id: string) => {
      const c = encounter.combatants.find((x) => x.id === id);
      if (!c) throw new EntryEditError('not_found', 'That combatant is no longer in the encounter.');
      return c;
    };
    const change = (result: { combatant: Combatant; event: string }) => {
      encounter = replace(encounter, result.combatant);
      saveEncounter(conn, encounter, now);
      pushToSheet(conn, encounter, result.combatant, now);
      events.push(result.event);
    };
    const d20 = () => 1 + options.random.nextInt(20);
    const initiativeFor = (entryId: string | null, name: string) => {
      const rolled = sheetInitiative(conn, entryId, options.random);
      if (!rolled) return null;
      rolls.push({ label: `${name}: Initiative`, result: rolled });
      return Number(rolled.total);
    };

    switch (command.kind) {
      case 'start': {
        const pulled = replaceAll(encounter, encounter.combatants.map((c) => pullFromSheet(conn, encounter, c)));
        if (pulled.combatants.length === 0) throw new EntryEditError('invalid', 'Add someone to the encounter first.');
        const step = startTurns(pulled);
        encounter = step.encounter;
        saveEncounter(conn, encounter, now);
        events.push(...step.events);
        break;
      }
      case 'next': {
        const ending = encounter.currentCombatantId;
        const step = nextTurn(encounter);
        encounter = step.encounter;
        saveEncounter(conn, encounter, now);
        // The combatant whose turn ended may have lost a timed condition.
        const ended = ending === null ? undefined : encounter.combatants.find((c) => c.id === ending);
        if (ended) pushToSheet(conn, encounter, ended, now);
        events.push(...step.events);
        break;
      }
      case 'previous':
      case 'end': {
        const step = command.kind === 'previous' ? previousTurn(encounter) : endTurns(encounter);
        encounter = step.encounter;
        saveEncounter(conn, encounter, now);
        events.push(...step.events);
        break;
      }
      case 'damage':
      case 'heal': {
        const c = combatant(command.combatantId);
        const amount = amountOf(command.amount, `${c.name}: ${command.kind === 'damage' ? 'damage' : 'healing'}`, options.random, rolls);
        change(command.kind === 'damage' ? damage(c, amount) : heal(c, amount));
        break;
      }
      case 'addCondition': {
        const name = javaStrip(command.name);
        if (name === '' || name.length > NAME_MAX) throw new EntryEditError('invalid', 'A condition needs a name.');
        if (!Number.isInteger(command.rounds) || command.rounds < 0 || command.rounds > 1000) throw new EntryEditError('invalid', 'A condition lasts a whole number of rounds.');
        change(addCondition(combatant(command.combatantId), condition(name, command.rounds)));
        break;
      }
      case 'removeCondition':
        change(removeCondition(combatant(command.combatantId), command.name));
        break;
      case 'setInitiative': {
        if (!Number.isInteger(command.initiative) || Math.abs(command.initiative) > 1000) throw new EntryEditError('invalid', 'Initiative is a whole number.');
        encounter = replace(encounter, { ...combatant(command.combatantId), initiative: command.initiative });
        saveEncounter(conn, encounter, now);
        break;
      }
      case 'addCombatant': {
        const d = cleanDetails(conn, command.details);
        let { initiative, maxHp, currentHp } = d;
        // Left at the form's defaults? Then a linked entry's sheet fills them in.
        if (initiative === 0) initiative = initiativeFor(d.linkedEntryId, d.name) ?? 0;
        if (maxHp === 10 && currentHp === 10) {
          const hp = sheetHitPoints(conn, d.linkedEntryId);
          if (hp) [currentHp, maxHp] = hp;
        }
        const added: Combatant = { id: newId(), ...d, initiative, maxHp: Math.max(0, maxHp), currentHp, position: encounter.combatants.length, conditions: [] };
        encounter = replaceAll(encounter, [...encounter.combatants, added]);
        saveEncounter(conn, encounter, now);
        break;
      }
      case 'updateCombatant': {
        const d = cleanDetails(conn, command.details);
        encounter = replace(encounter, { ...combatant(command.combatantId), ...d, maxHp: Math.max(0, d.maxHp) });
        saveEncounter(conn, encounter, now);
        break;
      }
      case 'removeCombatant': {
        combatant(command.combatantId);
        encounter = replaceAll(encounter, encounter.combatants.filter((c) => c.id !== command.combatantId));
        saveEncounter(conn, encounter, now);
        break;
      }
      case 'rollInitiative': {
        encounter = replaceAll(
          encounter,
          encounter.combatants.map((c) => ({ ...c, initiative: initiativeFor(c.linkedEntryId, c.name) ?? d20() })),
        );
        saveEncounter(conn, encounter, now);
        events.push(`Initiative: ${turnOrder(encounter.combatants).map((c) => `${c.name} ${c.initiative}`).join(', ')}.`);
        break;
      }
      case 'quickAdd': {
        const creatures = conn.all("SELECT id, title FROM lore_entries WHERE entity_type = 'CREATURE' ORDER BY title COLLATE NOCASE").map((r) => ({ id: text(r['id']), title: text(r['title']) }));
        if (creatures.length === 0) throw new EntryEditError('invalid', 'The realm has no Creature entries yet. Make some first, then add them here.');
        const count = Math.min(1 + options.random.nextInt(3), creatures.length);
        // A shuffle, as `Collections.shuffle` does it.
        for (let i = creatures.length - 1; i > 0; i--) {
          const j = options.random.nextInt(i + 1);
          [creatures[i], creatures[j]] = [creatures[j]!, creatures[i]!];
        }
        const added: Combatant[] = [];
        for (const pick of creatures.slice(0, count)) {
          const initiative = initiativeFor(pick.id, pick.title) ?? d20();
          const [currentHp, maxHp] = sheetHitPoints(conn, pick.id) ?? [10, 10];
          added.push({ id: newId(), name: pick.title, linkedEntryId: pick.id, initiative, maxHp: Math.max(0, maxHp), currentHp, playerCharacter: false, notes: '', position: encounter.combatants.length + added.length, conditions: [] });
        }
        encounter = replaceAll(encounter, [...encounter.combatants, ...added]);
        saveEncounter(conn, encounter, now);
        events.push(`${added.map((c) => c.name).join(', ')} join${added.length === 1 ? 's' : ''} the fight.`);
        break;
      }
    }
    for (const line of events) appendToPlayLog(conn, campaignId, line, options.stamp, now);
  });
  return { events, rolls };
}

/** A number, or a roll rolled now; refused if neither. */
function amountOf(typed: string, label: string, random: Random, rolls: { label: string; result: RollResult }[]): number {
  const trimmed = javaStrip(typed);
  if (trimmed === '') throw new EntryEditError('invalid', 'Type a number, or a roll like 2d6+3.');
  if (/^[+-]?\d{1,6}$/.test(trimmed)) return Math.abs(Number(trimmed));
  try {
    const result = DiceExpression.parse(trimmed).roll(random);
    rolls.push({ label, result });
    return Math.min(1_000_000, Math.abs(Number(result.total)));
  } catch (error) {
    if (error instanceof DiceError) throw new EntryEditError('invalid', error.message);
    throw error;
  }
}

// ---- the party (PartyPanel) ----

/**
 * One more or one less of a resource on a sheet, between 0 and its maximum; a hit point is
 * logged. Anyone in the running encounter who shares the sheet follows it.
 */
export function stepSheetResource(conn: SqlConnection, campaignId: string, sheetId: string, fieldKey: string, delta: -1 | 1, stamp: string, now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    const s = sheetById(conn, sheetId);
    const field = allFields(s.template).find((f) => f.key === fieldKey && f.type === 'RESOURCE');
    if (!field) throw new EntryEditError('not_found', 'That sheet has no such resource.');
    const ev = evaluator(s);
    const value = ev.number(fieldKey);
    const max = ev.max(fieldKey);
    if (value === null || max === null || max <= 0n) return;
    const next = BigInt(Math.max(0, Math.min(Number(max), Number(value) + delta)));
    if (next === value) return;
    setSheetValues(conn, sheetId, new Map(s.sheet.values).set(fieldKey, String(next)), now);
    if (fieldFor(s.template, 'HIT_POINTS')?.key === fieldKey) {
      appendToPlayLog(conn, campaignId, `${s.sheet.name}${delta < 0 ? ' loses 1 hit point' : ' regains 1 hit point'} (${next}/${max}).`, stamp, now);
    }
    followSheet(conn, campaignId, s.sheet.entryId, now);
  });
}

/** A sheet's conditions, set from the party panel, with the line for the play log. */
export function setSheetConditions(conn: SqlConnection, campaignId: string, sheetId: string, conditions: readonly Condition[], line: string, stamp: string, now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    const s = sheetById(conn, sheetId);
    const values = new Map(s.sheet.values);
    const encoded = encodeConditions(conditions.map((k) => condition(k.name, k.roundsLeft)));
    if (encoded === '') values.delete(CONDITIONS_KEY);
    else values.set(CONDITIONS_KEY, encoded);
    setSheetValues(conn, sheetId, values, now);
    if (line !== '') appendToPlayLog(conn, campaignId, line, stamp, now);
    followSheet(conn, campaignId, s.sheet.entryId, now);
  });
}

/** After a sheet changes, anyone in the campaign's running encounter who shares it follows it. */
function followSheet(conn: SqlConnection, campaignId: string, entryId: string | null, now: string): void {
  const activeId = getCampaign(conn, campaignId)?.activeEncounterId ?? null;
  const encounter = activeId === null || entryId === null ? null : getEncounter(conn, activeId);
  if (!encounter) return;
  let changed = false;
  const combatants = encounter.combatants.map((c) => {
    if (c.linkedEntryId !== entryId || !sharesSheet(encounter, c)) return c;
    changed = true;
    return pullFromSheet(conn, encounter, c);
  });
  if (changed) saveEncounter(conn, { ...encounter, combatants }, now);
}

function sheetById(conn: SqlConnection, id: string): SheetAndTemplate {
  const sheet = getSheet(conn, id);
  const template = sheet ? getTemplate(conn, sheet.templateId) : null;
  if (!sheet || !template) throw new EntryEditError('not_found', 'That character sheet no longer exists.');
  return { sheet, template };
}

// ---- rows and checks ----

const optional = (value: unknown) => (value === null || value === undefined || String(value).trim() === '' ? null : String(value));

function combatantOf(row: SqlRow): Combatant {
  return {
    id: text(row['id']),
    name: text(row['name']),
    linkedEntryId: optional(row['linked_entry_id']),
    initiative: int(row['initiative']),
    maxHp: Math.max(0, int(row['max_hp'])),
    currentHp: int(row['current_hp']),
    playerCharacter: int(row['is_player_character']) !== 0,
    notes: text(row['notes']),
    position: int(row['position']),
    conditions: decodeConditions(text(row['conditions'])).filter((k) => k.name !== ''),
  };
}

const replace = (encounter: Encounter, changed: Combatant): Encounter => replaceAll(encounter, encounter.combatants.map((c) => (c.id === changed.id ? changed : c)));
const replaceAll = (encounter: Encounter, combatants: readonly Combatant[]): Encounter => ({ ...encounter, combatants });

function cleanDetails(conn: SqlConnection, d: CombatantDetails): CombatantDetails {
  if (d.name.length > NAME_MAX) throw new EntryEditError('invalid', `A name can be at most ${NAME_MAX} characters.`);
  if (d.notes.length > NOTES_MAX) throw new EntryEditError('invalid', `Notes can be at most ${NOTES_MAX.toLocaleString('en')} characters.`);
  for (const n of [d.initiative, d.maxHp, d.currentHp]) {
    if (!Number.isInteger(n) || Math.abs(n) > HP_LIMIT) throw new EntryEditError('invalid', 'Initiative and hit points are whole numbers.');
  }
  if (d.linkedEntryId !== null && int(scalar(conn, 'SELECT COUNT(*) FROM lore_entries WHERE id = ?', [d.linkedEntryId])) === 0) {
    throw new EntryEditError('not_found', 'That entry no longer exists.');
  }
  return { ...d, name: javaStrip(d.name) === '' ? 'Combatant' : d.name.replace(/[\r\n]+/g, ' ') };
}
